import { Modal } from "@fluentui/react";
import classnames from "classnames";
import moment from "moment";
import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import { CalendarEventView } from "src/calendar";
import { localize } from "src/l10n";
import { Label, UnstyledButton } from "src/ui";

export interface IUpcomingEvent {
    id: number;
    title: string;
    start: string;
    allDay: boolean;
}

export interface IUpcomingEventsWidgetData {
    events?: IUpcomingEvent[];
}

export interface IUpcomingEventsWidgetProps {
    data: IUpcomingEventsWidgetData;
}

const UpcomingEventsWidget: FunctionComponent<IUpcomingEventsWidgetProps> = ({ data }) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [canScrollRight, setCanScrollRight] = useState<boolean>(false);
    const [openEventId, setOpenEventId] = useState<number | undefined>(undefined);

    const events: IUpcomingEvent[] = Array.isArray(data?.events) ? data.events : [];
    const eventCount = events.length;

    // Shows the event in the same modal the calendar uses, so nobody has to leave
    // the start page to read it.
    const onEventClick = useCallback(
        (event?: IUpcomingEvent) => setOpenEventId(event?.id),
        [setOpenEventId],
    );

    const onEventDismiss = useCallback(
        () => setOpenEventId(undefined),
        [setOpenEventId],
    );

    // A vertical wheel scrolls the row sideways. Without this there is no way to
    // reach the later events with a plain mouse, since the scrollbar is hidden.
    // The listener has to be registered natively: React treats onWheel as passive,
    // so preventDefault would be ignored there.
    useEffect(
        () => {
            const element = scrollRef.current;
            if (!element) {
                return;
            }

            const onWheel = (event: WheelEvent) => {
                if (element.scrollWidth <= element.clientWidth) {
                    return;
                }

                // Leave trackpad gestures that are already horizontal alone.
                if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) {
                    return;
                }

                const scrollLeftBefore = element.scrollLeft;

                element.scrollLeft += event.deltaY;

                // Only swallow the wheel event if the row actually moved. At either
                // end the assignment above is clamped, and preventing the default
                // there would trap the page scroll whenever the pointer happens to
                // be over the widget.
                if (element.scrollLeft !== scrollLeftBefore) {
                    event.preventDefault();
                }
            };

            element.addEventListener("wheel", onWheel, { passive: false });

            return () => element.removeEventListener("wheel", onWheel);
        },
        [eventCount],
    );

    // Drives the fade on the right edge, which is hidden once the row is scrolled
    // all the way to the end.
    useEffect(
        () => {
            const element = scrollRef.current;
            if (!element) {
                return;
            }

            const update = () => setCanScrollRight(
                element.scrollLeft + element.clientWidth < element.scrollWidth - 1
            );

            update();

            element.addEventListener("scroll", update);
            window.addEventListener("resize", update);

            return () => {
                element.removeEventListener("scroll", update);
                window.removeEventListener("resize", update);
            };
        },
        [eventCount],
    );

    if (eventCount === 0) {
        return (
            <div className="UpcomingEventsWidget empty">
                <Label
                    as="div"
                    color="visageGray3"
                    size="body-2"
                >
                    {localize("INGA_KOMMANDE_HANDELSER")}
                </Label>
            </div>
        );
    }

    return (
        <div className="UpcomingEventsWidget">
            <div className={classnames("events-viewport", { "can-scroll-right": canScrollRight })}>
                <div className="events" ref={scrollRef}>
                    {events.map((event: IUpcomingEvent) => (
                        <UnstyledButton
                            className="event"
                            key={event.id}
                            onClick={onEventClick}
                            onClickData={event}
                            title={event.title}
                        >
                            <div className="date-badge">
                                <Label
                                    as="div"
                                    className="day"
                                    color="visageGray"
                                    size="body-1"
                                    weight="bold"
                                >
                                    {moment(event.start).format("D")}
                                </Label>
                                <Label
                                    as="div"
                                    className="month"
                                    color="visageGray3"
                                    size="body-3"
                                    uppercase
                                >
                                    {moment(event.start).format("MMM")}
                                </Label>
                            </div>
                            <div className="details">
                                <Label
                                    as="div"
                                    className="title"
                                    color="visageGray"
                                    size="body-2"
                                    weight="medium"
                                >
                                    {event.title}
                                </Label>
                                <Label
                                    as="div"
                                    className="time"
                                    color="visageGray3"
                                    size="body-2"
                                >
                                    {event.allDay
                                        ? localize("Heldag")
                                        : moment(event.start).format("HH:mm")}
                                </Label>
                            </div>
                        </UnstyledButton>
                    ))}
                </div>
            </div>
            {openEventId !== undefined && (
                <Modal
                    className="spintr-modal CalendarEventViewModal"
                    allowTouchBodyScroll
                    isOpen={true}
                    onDismiss={onEventDismiss}
                >
                    <CalendarEventView
                        id={openEventId.toString()}
                        onDismiss={onEventDismiss}
                    />
                </Modal>
            )}
        </div>
    );
};

export default UpcomingEventsWidget;
